import type FullCalendar from "@fullcalendar/react";
import { useState, useRef } from "react";

import { globalSearchConfig } from "../../../../config/search/global-search-config";

// Import modular components
import { CalendarAlerts } from "@/components/calendar/CalendarAlerts";
import { CalendarControls } from "@/components/calendar/CalendarControls";
import { CalendarFilterSidebar } from "@/components/calendar/CalendarFilterSidebar";
import { CalendarMain } from "@/components/calendar/CalendarMain";
import { CalendarMapView } from "@/components/calendar/CalendarMapView";
import { EventDetailsSidebar } from "@/components/calendar/EventDetailsSidebar";
import {
  ErrorBoundary,
  CalendarErrorFallback,
} from "@/components/ErrorBoundary";
import { useCalendarEvents } from "@/hooks/useCalendarEvents";
import { useCalendarFilters } from "@/hooks/useCalendarFilters";
import { useCollapsibleSidebar } from "@/hooks/useCollapsibleSidebar";
import { useDebounce } from "@/hooks/useDebounce";
import type {
  FilterType,
  WeekFilter,
  ProcessedEvent,
  CalendarView,
} from "@/types/calendar";
import { convertToFullCalendarEvent } from "@/utils/calendar.utils";

import "@/styles/calendar.css";

const CalendarNew = () => {
  // State management
  const [searchInput, setSearchInput] = useState(""); // Raw search input
  const [selectedEvent, setSelectedEvent] = useState<ProcessedEvent | null>(
    null,
  );
  const [activeFilter, setActiveFilter] = useState<FilterType>("upcoming");
  const [weekFilter, setWeekFilter] = useState<WeekFilter>("thisWeek");
  const [currentView, setCurrentView] = useState<CalendarView>("month");
  const calendarRef = useRef<FullCalendar>(null);

  // Debounce search input using global config
  const searchQuery = useDebounce(searchInput, globalSearchConfig.debounceMs);

  // Collapsible sidebar hooks
  const leftSidebar = useCollapsibleSidebar({
    defaultOpen: true,
    enableKeyboard: true,
    responsive: true,
  });

  const rightSidebar = useCollapsibleSidebar({
    defaultOpen: false,
    enableKeyboard: true,
    responsive: true,
  });

  // Data fetching
  const { events, isLoading, isError, error, isCalendarEnabled } =
    useCalendarEvents();

  // Apply filters
  const { searchResults, filteredEvents } = useCalendarFilters({
    events,
    activeFilter,
    weekFilter,
    searchQuery,
  });

  // Determine which events to display
  const displayedEvents = searchQuery ? searchResults : filteredEvents;
  const calendarEvents = displayedEvents.map(convertToFullCalendarEvent);

  // Event handlers
  const handleEventClick = (clickInfo: any) => {
    const eventData = clickInfo.event.extendedProps.originalEvent;
    setSelectedEvent(eventData);
    rightSidebar.open();
  };

  const handleDateClick = (_selectInfo: any) => {
    // Date click handler — no-op for now
  };

  const handleViewChange = (view: CalendarView) => {
    setCurrentView(view);
    // Only change FullCalendar view if not in map mode
    if (view !== "map") {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.changeView(
          view === "month"
            ? "dayGridMonth"
            : view === "week"
              ? "timeGridWeek"
              : view === "day"
                ? "timeGridDay"
                : "listMonth",
        );
      }
    }
  };

  const handleEventSelect = (event: ProcessedEvent) => {
    setSelectedEvent(event);
    rightSidebar.open();
  };

  // Close right sidebar when selecting null
  const handleSidebarClose = () => {
    rightSidebar.close();
    setTimeout(() => setSelectedEvent(null), 300);
  };

  return (
    <div
      className="flex flex-col calendar-page-container"
      style={{
        height: "calc(100vh - 6rem)",
        background:
          "linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0f0f1e 100%)",
      }}
    >
      {/* Header removed */}

      {/* Alerts */}
      <CalendarAlerts
        isError={isError}
        error={error}
        isCalendarEnabled={isCalendarEnabled}
      />

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden relative">
        {/* Left Filter Sidebar */}
        <CalendarFilterSidebar
          isOpen={leftSidebar.isOpen}
          onToggle={leftSidebar.toggle}
          searchQuery={searchInput}
          onSearchChange={setSearchInput}
          weekFilter={weekFilter}
          onWeekFilterChange={setWeekFilter}
          displayedEvents={displayedEvents}
          isLoading={isLoading}
          selectedEvent={selectedEvent}
          onEventSelect={handleEventSelect}
          isMobile={leftSidebar.isMobile}
          debouncedSearchQuery={searchQuery}
        />

        {/* Main Calendar Content - Flexes when sidebars open */}
        <div
          className="h-full p-2 md:p-4 transition-all duration-300"
          style={{
            marginLeft:
              leftSidebar.isOpen && !leftSidebar.isMobile
                ? `${leftSidebar.sidebarWidth}px`
                : 0,
            marginRight:
              rightSidebar.isOpen && !rightSidebar.isMobile
                ? `${rightSidebar.sidebarWidth}px`
                : 0,
          }}
        >
          <div className="h-full flex flex-col">
            {/* Calendar Controls */}
            <CalendarControls
              currentView={currentView}
              onViewChange={handleViewChange}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
            />

            {/* Calendar Component or Map View */}
            {currentView === "map" ? (
              <CalendarMapView
                events={displayedEvents}
                onEventClick={handleEventSelect}
              />
            ) : (
              <CalendarMain
                events={calendarEvents}
                isLoading={isLoading}
                onEventClick={handleEventClick}
                onDateClick={handleDateClick}
                calendarRef={calendarRef}
                currentView={currentView}
              />
            )}
          </div>
        </div>

        {/* Right Event Details Sidebar */}
        {rightSidebar.isMobile && rightSidebar.isOpen && (
          <div
            className="fixed inset-0 bg-black/25 z-[999]"
            onClick={handleSidebarClose}
          />
        )}
        <div
          className={`
            ${
              rightSidebar.isMobile
                ? "fixed inset-y-0 right-0 z-[1000]"
                : "absolute right-0 top-0 bottom-0 z-10"
            }
            bg-white border-l shadow-lg
            transition-all duration-300
            overflow-hidden
          `}
          style={{
            width: rightSidebar.isOpen
              ? rightSidebar.isMobile
                ? "100%"
                : `${rightSidebar.sidebarWidth}px`
              : 0,
          }}
        >
          {selectedEvent && (
            <ErrorBoundary
              fallback={
                <CalendarErrorFallback onReset={() => setSelectedEvent(null)} />
              }
              onReset={() => setSelectedEvent(null)}
            >
              <EventDetailsSidebar
                event={selectedEvent}
                isOpen={rightSidebar.isOpen}
                onClose={handleSidebarClose}
              />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarNew;
